﻿import { ensureTelerikPopupRevealed } from './popupreveal.js';

// One entry per live dropdown, keyed by its anchor selector so a disposed one can be removed again.
// Refs used to be kept forever: every dropdown from every page you had visited stayed registered, and
// closePopup() below calls all of them. Those calls race the dropdown you are opening, so it closes
// itself as it opens.
var savedDropdowns = [];
var savedPopupSelectors = [];
var savedButtonSelectors = [];

window.saveDotNet = function (dotNetRef, key) {
    window.forgetDotNet(key);
    savedDropdowns.push({ key: key, ref: dotNetRef });
}

window.forgetDotNet = function (key) {
    savedDropdowns = savedDropdowns.filter(entry => entry.key !== key);
}

window.saveSelectors = function (popupSelector, buttonSelector) {
    savedPopupSelectors.push(popupSelector);
    savedButtonSelectors.push(buttonSelector);
}

window.replaceSelectors = function (popupSelector, buttonSelector) {
    savedPopupSelectors = [popupSelector];
    savedButtonSelectors = [buttonSelector];
}

window.closePopup = function () {
    // Swallow rejections from a ref whose circuit has gone, so one dead dropdown cannot stop the rest.
    savedDropdowns.forEach(entry => entry.ref.invokeMethodAsync("ClosePopup", true).catch(() => { }));
}

// Each open dropdown is asked to close on its own terms, working from savedDropdowns rather than the
// single tracked selector. Two things were wrong with the old version:
//
//  * it closed every dropdown, and pointerdown fires before click — so clicking a second dropdown's
//    anchor told that dropdown to close, and the close could land after the click had opened it;
//  * only the most recently opened popup was tracked, so an earlier one that was still open had
//    nothing watching it and could never be closed by clicking away.
//
// A dropdown's key is its anchor selector, which gives both the anchor (.key) and popup (.key-popup).
document.addEventListener("pointerdown", (e) => {
    // Touch/pen presses and presses on SVG icons (grid action cells are full of them) still give an
    // Element, but guard anyway — without a target there is nothing to compare against, and closing
    // every open dropdown on an untargeted press is the behaviour this handler exists to avoid.
    if (!(e.target instanceof Element)) {
        return;
    }

    savedDropdowns.forEach(entry => {
        const popupElement = document.querySelector(`.${entry.key}-popup`);

        // Not open — nothing to close.
        if (!popupElement || popupElement.offsetHeight === 0) {
            return;
        }

        // Inside the dropdown (its own popup/anchor, or a submenu opened from it): the click belongs
        // to this dropdown, and closing here would fight it.
        if (clickedInsideDropdown(e.target, entry.key, new Set())) {
            return;
        }

        entry.ref.invokeMethodAsync("ClosePopup", true).catch(() => { });
    });
});

// Whether the click landed inside the dropdown identified by `key` — counting its own anchor and
// popup, plus the popup of any dropdown nested within it at any depth (e.g. an EBizChevronDropdown's
// flyout).
//
// Nested popups have to be resolved through the DOM rather than by containment: TelerikPopup renders
// every popup into the Telerik root container at the end of <body>, so a flyout item is not a DOM
// descendant of the parent dropdown's popup. Without this, pressing a flyout item looked like an
// outside click to the parent, which closed on pointerdown and tore the item out from under the
// click that was still to come — the click never registered and the menu just vanished.
//
// A nested dropdown is one whose *anchor* is rendered inside this dropdown's popup; from there the
// same rule applies to its own popup, so submenus of submenus resolve by recursion. `visited` guards
// against a cycle in the anchor/popup graph.
function clickedInsideDropdown(target, key, visited) {
    if (visited.has(key)) {
        return false;
    }
    visited.add(key);

    if (target.closest(`.${key}`) || target.closest(`.${key}-popup`)) {
        return true;
    }

    const popupElement = document.querySelector(`.${key}-popup`);
    if (!popupElement) {
        return false;
    }

    return savedDropdowns.some(entry =>
        entry.key !== key &&
        popupElement.querySelector(`.${entry.key}`) &&
        clickedInsideDropdown(target, entry.key, visited));
}

// Reveals a dropdown popup that Telerik showed but never made visible.
//
// On most mobile-card-layout page loads (measured: 6 of 8 in real Chrome, 0 of 8 on the same grid in
// desktop layout) `PopupRef.Show()` returns fine — the C# side has a live ref and no exception — yet the
// popup keeps its pre-show state: the outer `.k-animation-container` stays at `left:-10000px;
// display:none` and Telerik's inner `.k-child-animation-container` stays `display:none`, so the items
// render but measure 0 and the menu is invisible. IsOpen still flips, so the next click "closes" it and
// the button looks dead. Not the fade animation (it reproduces identically with AnimationType.None) and
// not our outside-click handler (no ClosePopup is invoked on those loads). It is sticky for the page
// load and affects every popup on the page at once, which is why it reads as "the export button is
// broken". Same failure mode as ensureDatePickerPopupOpen in datepicker.js, which this mirrors.
//
// Placement: 'flyout' puts the popup beside its anchor (EBizChevronDropdown), anything else puts it
// below. `alignRight` matches the popup's right edge to the anchor's, which is the dropdown default.
window.ensureDropdownPopupOpen = function (anchorSelector, placement, alignRight, horizontalOffset, verticalOffset) {
    const popup = document.querySelector(`.${anchorSelector}-popup`);
    const anchor = document.querySelector(`.${anchorSelector}:not(.k-animation-container)`);
    if (!popup || !anchor) return;

    horizontalOffset = horizontalOffset || 0;
    verticalOffset = verticalOffset || 0;

    const position = () => {
        const r = anchor.getBoundingClientRect();
        const box = popup.querySelector(".k-popup");
        const height = box ? box.offsetHeight : popup.offsetHeight;
        const width = box ? box.offsetWidth : popup.offsetWidth;

        let left;
        let top;

        if (placement === "flyout") {
            // Beside the anchor, top-aligned; swap to the left side when there is no room right.
            const spaceRight = window.innerWidth - r.right;
            left = (spaceRight < width && r.left > width)
                ? r.left + window.scrollX - width - horizontalOffset
                : r.right + window.scrollX + horizontalOffset;
            top = r.top + window.scrollY + verticalOffset;
        } else {
            left = alignRight
                ? r.right + window.scrollX - width + horizontalOffset
                : r.left + window.scrollX + horizontalOffset;
            // Below the anchor, flipping above when it would not fit — Telerik's own Flip collision.
            const spaceBelow = window.innerHeight - r.bottom;
            top = (spaceBelow < height + verticalOffset && r.top > height + verticalOffset)
                ? r.top + window.scrollY - height - verticalOffset
                : r.bottom + window.scrollY + verticalOffset;
        }

        // Keep it on screen horizontally; a popup pushed off the right edge is as unusable as a
        // hidden one.
        left = Math.max(window.scrollX, Math.min(left, window.scrollX + window.innerWidth - width));

        popup.style.left = Math.round(left) + "px";
        popup.style.top = Math.round(top) + "px";
    };

    // Only step in once Telerik has plainly failed. The reveal normally lands ~110ms after Show()
    // (100ms fade + a frame), so without a grace period this would rescue every healthy popup too —
    // taking over placement from Telerik everywhere and, for a hover-opened chevron flyout, moving
    // the popup out from under the pointer, which closes it again.
    ensureTelerikPopupRevealed(popup, position, { graceMs: 300, giveUpMs: 2000 });
}

window.checkScrollbar = function () {
    var popupElements = savedPopupSelectors.map(selector => document.querySelector(`${selector} .dropdown-popup-inner`));

    popupElements.forEach(popupElement => {
        if (popupElement) {
            const hasScrollbar = popupElement.scrollHeight > popupElement.clientHeight;

            if (hasScrollbar) {
                popupElement.classList.add('scrollbar-padding');
            }
            else {
                popupElement.classList.remove('scrollbar-padding');
            }
        }
    });
}

// Accessibility

window.focusFirstItemInDropdown = function (anchorSelector) {
    var popupRoot = document.querySelector(`.${anchorSelector}-popup`);
    if (!popupRoot) return;

    const popup = popupRoot.querySelector(".dropdown-popup-inner");
    if (!popup) return;

    const first = popup.querySelector(":scope > .ebiz-dropdown-item, :scope > .chevron-dropdown-anchor");
    if (first) first.focus();
}

window.focusNextItemInDropdown = function (anchorSelector) {
    const items = document.querySelectorAll(
        `.${anchorSelector}-popup .dropdown-popup-inner .ebiz-dropdown-item.selectable,
         .${anchorSelector}-popup .dropdown-popup-inner .chevron-dropdown-anchor`
    );
    const active = document.activeElement;
    let idx = Array.from(items).indexOf(active);
    if (idx >= 0 && idx < items.length - 1) {
        items[idx + 1].focus();
    }
};

window.focusPreviousItemInDropdown = function (anchorSelector) {
    const items = document.querySelectorAll(
        `.${anchorSelector}-popup .dropdown-popup-inner .ebiz-dropdown-item.selectable,
         .${anchorSelector}-popup .dropdown-popup-inner .chevron-dropdown-anchor`
    );
    const active = document.activeElement;
    let idx = Array.from(items).indexOf(active);
    if (idx > 0) {
        items[idx - 1].focus();
    }
};

window.addArrowKeyGuardDropdown = function (anchorSelector) {
    var popupRoot = document.querySelector(`.${anchorSelector}-popup`);
    if (!popupRoot) return;

    const popup = popupRoot.querySelector(".dropdown-popup-inner");
    if (!popup || popup._arrowGuardAttached) return;

    const handler = (e) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
        }
    }

    popup.addEventListener("keydown", handler, true);

    popup._arrowGuardAttached = true;
    popup._arrowGuardHandler = handler;
}


window.removeArrowKeyGuardDropdown = function (anchorSelector) {
    var popupRoot = document.querySelector(`.${anchorSelector}-popup`);
    if (!popupRoot) return;

    const popup = popupRoot.querySelector(".dropdown-popup-inner");
    if (!popup || !popup._arrowGuardAttached) return;

    popup.removeEventListener("keydown", popup._arrowGuardHandler, true);
    popup._arrowGuardAttached = false;
    popup._arrowGuardHandler = null;
};

window.focusAnchor = function (anchorSelector) {
    const anchor = document.querySelector(`.${anchorSelector}`);
    if (!anchor) return;
    // Focus the anchor directly if it's an inherently focusable element, otherwise
    // fall back to the first focusable descendant (e.g., a <button> inside a wrapper div).
    const focusTarget = anchor.matches('button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])')
        ? anchor
        : anchor.querySelector('button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    focusTarget?.focus?.();
}

// Overflow tooltip for truncated chevron dropdown items (mirrors window.gridTooltip).
// On hover of a `.dropdown-truncate` element whose text overflows, sets the element's
// `title` attribute and the `.dropdown-item-tooltip-target` class so the EBizTooltip
// (TargetSelector=".dropdown-item-tooltip-target") reveals it. Does nothing when the
// text fits, so no tooltip appears for non-truncated items.
let dropdownTooltipTarget = null;

window.dropdownTooltip = {
    handleMouseEnter: function (clientX, clientY) {
        const el = document.elementFromPoint(clientX, clientY);
        if (!el) return;

        const target = el.closest('.dropdown-truncate');
        if (!target) return;

        dropdownTooltipTarget = target;

        if (findOverflowingNode(target)) {
            target.setAttribute('title', target.textContent.trim());
            target.classList.add('dropdown-item-tooltip-target');

            // The real mouseenter already fired before the target class existed, so
            // re-dispatch it for the Telerik tooltip to bind to the element.
            setTimeout(() => {
                target.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
            }, 0);
        }
    },

    handleMouseLeave: function () {
        if (dropdownTooltipTarget) {
            dropdownTooltipTarget.classList.remove('dropdown-item-tooltip-target');
            dropdownTooltipTarget.removeAttribute('title');
            dropdownTooltipTarget = null;
        }
    }
};

// Returns the element (the root itself or a descendant) whose text is clipped, or null.
function findOverflowingNode(root) {
    if (root.scrollWidth > root.clientWidth) return root;
    for (const node of root.querySelectorAll('*')) {
        if (node.textContent && node.textContent.trim().length && node.scrollWidth > node.clientWidth) {
            return node;
        }
    }
    return null;
}

// Accessibility